import React, { useMemo, useState } from "react";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  CaretDown, CaretUp, Copy, X, DotsSixVertical, Star,
  ArrowsOut, NotePencil, FolderSimplePlus, ChartLineUp,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import { normalizeVideoUrl, isEmbeddableVideoUrl, isVideoHostUrl } from "@/lib/videoUrl";
import { SECTION_TYPES, calcSessionMetrics, groupBySection, libraryToExercise } from "./constants";
import { CopyExerciseToDayDialog, CopyDayToDayDialog } from "./CopyDialogs";
import { ProgressionSchemeDialog } from "./ProgressionSchemeDialog";

export function SessionBuilder({
  day, dayIndex, totalDays, weekDays, programId, totalWeeks,
  onUpdateName, onUpdateSessionType, onToggleRest, onRemove, onDuplicate, onMoveUp, onMoveDown,
  onReplaceExercises, onAddEx, onCopyExercise, onCopyDayToDay, onMirrorDayAcrossWeeks, onSchemeApplied, onFavoritesChanged, sessionTypes,
  allDays,
}) {
  const testid = `day-w${day.week}-d${day.day}`;
  const [collapsedSections, setCollapsedSections] = useState({});
  const [expanded, setExpanded] = useState({});
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [copyPicker, setCopyPicker] = useState(null);
  const [schemeTarget, setSchemeTarget] = useState(null);
  const [copyMenuOpen, setCopyMenuOpen] = useState(false);
  const [copyDayPicker, setCopyDayPicker] = useState(false);

  const groups = useMemo(() => groupBySection(day.exercises || []), [day.exercises]);
  const usedSections = SECTION_TYPES.filter((s) => groups[s.id]?.length);
  const availableSections = SECTION_TYPES.filter((s) => !groups[s.id]?.length);
  const metrics = useMemo(() => calcSessionMetrics(day.exercises, day.session_type), [day.exercises, day.session_type]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const patch = (idx, key, val) => {
    const next = (day.exercises || []).map((ex, i) => (i === idx ? { ...ex, [key]: val } : ex));
    onReplaceExercises(next);
  };

  const remove = (idx) => onReplaceExercises((day.exercises || []).filter((_, i) => i !== idx));

  // ---------- HTML5 drop target for Exercise Library palette ----------
  const [dragOver, setDragOver] = useState(false);
  const handleDragEnter = (e) => {
    if (day.is_rest) return;
    if ((e.dataTransfer?.types || []).includes("application/x-edge-library-exercise")) {
      e.preventDefault();
      setDragOver(true);
    }
  };
  const handleDragOver = (e) => {
    if (day.is_rest) return;
    if ((e.dataTransfer?.types || []).includes("application/x-edge-library-exercise")) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    }
  };
  const handleDragLeave = (e) => {
    // Only clear when leaving the outermost container
    if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
  };
  const handleDrop = (e) => {
    setDragOver(false);
    if (day.is_rest) return;
    const raw = e.dataTransfer?.getData("application/x-edge-library-exercise");
    if (!raw) return;
    e.preventDefault();
    try {
      const lib = JSON.parse(raw);
      const ex = libraryToExercise(lib);
      const next = [...(day.exercises || []), ex];
      onReplaceExercises(next);
      // Toast happens in palette so UX stays consistent
    } catch (err) {
      console.error("bad library drop payload", err);
    }
  };

  const duplicate = (idx) => {
    const src = day.exercises[idx];
    const next = [...day.exercises];
    next.splice(idx + 1, 0, { ...src, favorited: false });
    onReplaceExercises(next);
  };

  const move = (idx, section) => patch(idx, "section", section);

  const toggleFav = async (idx) => {
    const ex = day.exercises[idx] || {};
    const nowFav = !ex.favorited;
    patch(idx, "favorited", nowFav);
    try {
      if (nowFav) {
        await api.post("/coach/favorites", {
          name: ex.name || "",
          section: ex.section || "strength",
          sets: ex.sets, reps: ex.reps, rest_sec: ex.rest_sec, tempo: ex.tempo,
          coach_cue: ex.coach_cue || ex.notes, alternatives: ex.alternatives,
          regression: ex.regression, progression: ex.progression, tags: ex.tags,
          video_url: ex.video_url, gif_url: ex.gif_url,
        });
        toast.success("Added to favorites");
      } else {
        await api.delete(`/coach/favorites/by-name?name=${encodeURIComponent(ex.name || "")}&section=${encodeURIComponent(ex.section || "strength")}`);
      }
      onFavoritesChanged?.();
    } catch {
      // silent — local flag already set
    }
  };

  const addToSection = (sectionId) => {
    const next = [...(day.exercises || []), { name: "", sets: 3, reps: "8-10", rest_sec: 90, section: sectionId }];
    onReplaceExercises(next);
    setAddSectionOpen(false);
  };

  const handleDragEnd = (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const [aSec, aIdxStr] = String(active.id).split("::");
    const [oSec, oIdxStr] = String(over.id).split("::");
    const aIdx = Number(aIdxStr);
    const oIdx = Number(oIdxStr);
    const list = [...(day.exercises || [])];
    if (aSec === oSec) {
      const sectionIds = list.map((e, i) => ({ i, ex: e })).filter((x) => (x.ex.section || "strength") === aSec).map((x) => x.i);
      const from = sectionIds.indexOf(aIdx);
      const to = sectionIds.indexOf(oIdx);
      if (from < 0 || to < 0) return;
      const slice = sectionIds.map((i) => list[i]);
      const moved = arrayMove(slice, from, to);
      sectionIds.forEach((absIdx, k) => { list[absIdx] = moved[k]; });
    } else {
      list[aIdx] = { ...list[aIdx], section: oSec };
    }
    onReplaceExercises(list);
  };

  return (
    <div
      className={`border ${day.is_rest ? "border-white/5 opacity-70" : (dragOver ? "border-yellow-400 border-dashed bg-yellow-400/5" : "border-white/10")} p-4 transition-colors relative`}
      data-testid={testid}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {dragOver && !day.is_rest && (
        <div
          className="absolute inset-0 pointer-events-none flex items-center justify-center z-10 border-2 border-dashed border-yellow-400 bg-yellow-400/10"
          data-testid={`${testid}-drop-hint`}
        >
          <span className="label-tiny text-yellow-400 tracking-widest">DROP TO ADD EXERCISE</span>
        </div>
      )}
      <div className="grid grid-cols-12 gap-2 mb-3 items-end">
        <div className="col-span-1">
          <label className="label-tiny block mb-1">DAY</label>
          <div className="h-9 flex items-center justify-center border border-white/10 bg-surface-elevated tabular-nums !text-yellow-400 font-heading">
            {day.day}
          </div>
        </div>
        <div className="col-span-6">
          <label className="label-tiny block mb-1">SESSION TITLE</label>
          <Input
            value={day.name}
            onChange={(e) => onUpdateName(e.target.value)}
            placeholder={day.is_rest ? "Rest Day" : "e.g. Upper Push"}
            className="bg-surface-elevated border-white/10 h-9"
            data-testid={`${testid}-name`}
          />
        </div>
        <div className="col-span-5 flex items-end justify-end gap-1">
          <button onClick={onToggleRest} data-testid={`${testid}-rest`} className={`px-2 py-1.5 label-tiny border transition-colors ${day.is_rest ? "!border-yellow-400 !text-yellow-400 !bg-yellow-400/5" : "border-white/10 text-text-secondary hover:text-white hover:border-white/40"}`}>REST</button>
          <button onClick={onMoveUp} disabled={dayIndex === 0} data-testid={`${testid}-up`} className="px-1.5 py-1.5 border border-white/10 text-text-secondary hover:text-white disabled:opacity-30 transition-colors" aria-label="Move up"><CaretUp size={12} /></button>
          <button onClick={onMoveDown} disabled={dayIndex === totalDays - 1} data-testid={`${testid}-down`} className="px-1.5 py-1.5 border border-white/10 text-text-secondary hover:text-white disabled:opacity-30 transition-colors" aria-label="Move down"><CaretDown size={12} /></button>
          <div className="relative">
            <button
              onClick={() => setCopyMenuOpen((v) => !v)}
              data-testid={`${testid}-copy-menu`}
              className={`inline-flex items-center gap-1 px-1.5 py-1.5 border transition-colors ${copyMenuOpen ? "!border-yellow-400 !text-yellow-400 !bg-yellow-400/10" : "border-white/10 text-text-secondary hover:!text-yellow-400 hover:!border-yellow-400"}`}
              aria-label="Copy day"
              title="Copy this day"
            >
              <Copy size={12} /><CaretDown size={9} />
            </button>
            {copyMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setCopyMenuOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-1 z-20 w-52 border border-white/15 bg-surface-DEFAULT shadow-xl"
                  data-testid={`${testid}-copy-menu-panel`}
                >
                  <button
                    onClick={() => { setCopyMenuOpen(false); onDuplicate?.(); }}
                    className="w-full text-left px-3 py-2 text-xs text-white hover:!bg-yellow-400/10 hover:!text-yellow-400 label-tiny tracking-wider transition"
                    data-testid={`${testid}-copy-clone-same-week`}
                  >
                    CLONE IN WEEK {day.week}
                  </button>
                  <button
                    onClick={() => { setCopyMenuOpen(false); setCopyDayPicker(true); }}
                    className="w-full text-left px-3 py-2 text-xs text-white hover:!bg-yellow-400/10 hover:!text-yellow-400 label-tiny tracking-wider transition border-t border-white/10"
                    data-testid={`${testid}-copy-to-day`}
                  >
                    TO DAY…
                  </button>
                  <button
                    onClick={() => { setCopyMenuOpen(false); onMirrorDayAcrossWeeks?.(); }}
                    className="w-full text-left px-3 py-2 text-xs text-white hover:!bg-yellow-400/10 hover:!text-yellow-400 label-tiny tracking-wider transition border-t border-white/10"
                    data-testid={`${testid}-copy-mirror-weeks`}
                  >
                    MIRROR ALL WEEKS
                  </button>
                </div>
              </>
            )}
          </div>
          <button onClick={onRemove} data-testid={`${testid}-remove`} className="px-1.5 py-1.5 border border-white/10 text-text-secondary hover:text-red-400 hover:border-red-400/40 transition-colors" aria-label="Remove session"><X size={12} /></button>
        </div>
      </div>

      {!day.is_rest && (
        <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid={`${testid}-session-type`}>
          <span className="label-tiny text-[10px] text-text-secondary">SESSION TYPE:</span>
          {(sessionTypes || []).map((t) => (
            <button key={t.id} type="button" onClick={() => onUpdateSessionType(t.id)} data-testid={`${testid}-session-type-${t.id}`}
              className={`px-2 py-1 text-[10px] label-tiny tracking-wider border transition ${(day.session_type || "strength") === t.id ? "!border-yellow-400 !bg-yellow-400/10 !text-yellow-400" : "border-white/10 text-text-secondary hover:border-white/40"}`}>
              {t.label.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      {!day.is_rest && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <div className="space-y-3">
            {usedSections.length === 0 && (
              <div className="border border-dashed border-white/10 p-6 text-center text-sm text-text-secondary">
                No sections yet. Add your first section below to start structuring this workout.
              </div>
            )}
            {usedSections.map((sec) => (
              <SectionBlock
                key={sec.id}
                testid={testid}
                section={sec}
                exercises={groups[sec.id] || []}
                collapsed={collapsedSections[sec.id]}
                onToggle={() => setCollapsedSections((c) => ({ ...c, [sec.id]: !c[sec.id] }))}
                expanded={expanded}
                setExpanded={setExpanded}
                onPatch={patch}
                onRemove={remove}
                onDuplicate={duplicate}
                onMove={move}
                onToggleFav={toggleFav}
                onCopyToDay={(absIdx) => setCopyPicker({ exercise: day.exercises[absIdx], sourceIdx: absIdx })}
                onScheme={(ex) => setSchemeTarget(ex)}
                onAddToSection={() => addToSection(sec.id)}
              />
            ))}
          </div>
        </DndContext>
      )}

      {!day.is_rest && (
        <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid={`${testid}-add-section-bar`}>
          <button
            onClick={() => setAddSectionOpen((v) => !v)}
            disabled={availableSections.length === 0}
            className="text-xs label-tiny tracking-wider inline-flex items-center gap-1.5 border border-white/10 px-3 py-1.5 hover:!border-yellow-400 hover:!text-yellow-400 transition disabled:opacity-40"
            data-testid={`${testid}-add-section-toggle`}
          >
            <FolderSimplePlus size={12} /> {addSectionOpen ? "CLOSE" : "ADD SECTION"}
          </button>
          {addSectionOpen && availableSections.map((s) => (
            <button key={s.id} onClick={() => addToSection(s.id)} data-testid={`${testid}-add-section-${s.id}`}
              className="text-[10px] label-tiny tracking-wider px-2.5 py-1.5 border border-white/10 hover:!border-yellow-400 hover:!text-yellow-400 transition inline-flex items-center gap-1">
              <s.icon size={10} weight="duotone" style={{ color: s.color }} /> {s.label.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      {!day.is_rest && (
        <div className="mt-4 pt-3 border-t border-white/10 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs" data-testid={`${testid}-metrics`}>
          <MetricPill label="VOLUME" value={metrics.volume} unit="reps" testid={`${testid}-metric-volume`} />
          <MetricPill label="TONNAGE" value={metrics.tonnage} unit="kg" testid={`${testid}-metric-tonnage`} />
          <MetricPill label="INTENSITY" value={metrics.intensity} unit="%" testid={`${testid}-metric-intensity`} />
          <MetricPill label="e1RM MAX" value={metrics.e1rm} unit="kg" testid={`${testid}-metric-e1rm`} />
          <MetricPill label="SESSION LOAD" value={metrics.sessionLoad} unit="AU" accent testid={`${testid}-metric-load`} />
        </div>
      )}

      {copyPicker && (
        <CopyExerciseToDayDialog
          exercise={copyPicker.exercise}
          weekDays={weekDays.filter((d) => !(d.week === day.week && d.day === day.day))}
          onCancel={() => setCopyPicker(null)}
          onConfirm={(targetDay) => {
            setCopyPicker(null);
            onCopyExercise?.({ sourceDayId: `${day.week}-${day.day}`, targetDay, exercise: { ...copyPicker.exercise } });
          }}
        />
      )}

      {copyDayPicker && (
        <CopyDayToDayDialog
          sourceDay={day}
          allDays={allDays || weekDays}
          onCancel={() => setCopyDayPicker(false)}
          onConfirm={(targetKey) => {
            setCopyDayPicker(false);
            onCopyDayToDay?.(targetKey);
          }}
        />
      )}

      {schemeTarget && (
        <ProgressionSchemeDialog
          programId={programId}
          totalWeeks={totalWeeks || day.week}
          sourceDay={day.day}
          sourceWeek={day.week}
          sourceSection={schemeTarget.section || "strength"}
          exerciseName={schemeTarget.name || ""}
          sessionType={day.session_type || "strength"}
          onClose={() => setSchemeTarget(null)}
          onApplied={() => { onSchemeApplied?.(); }}
        />
      )}
    </div>
  );
}

/* ------------ Section block (droppable + collapsible) ------------ */

function SectionBlock({ testid, section, exercises, collapsed, onToggle, expanded, setExpanded, onPatch, onRemove, onDuplicate, onMove, onToggleFav, onCopyToDay, onAddToSection, onScheme }) {
  const ids = exercises.map((ex) => `${section.id}::${ex.__idx}`);
  return (
    <div className="border border-white/10 bg-surface-elevated/30" data-testid={`${testid}-section-${section.id}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/5" style={{ background: `linear-gradient(90deg, ${section.color}10, transparent)` }}>
        <button onClick={onToggle} className="inline-flex items-center gap-2 flex-1 text-left" data-testid={`${testid}-section-${section.id}-toggle`}>
          <section.icon size={14} weight="duotone" style={{ color: section.color }} />
          <span className="font-heading text-sm">{section.label.toUpperCase()}</span>
          <span className="text-[10px] text-text-secondary tabular-nums">({exercises.length})</span>
        </button>
        <div className="flex items-center gap-1">
          <button onClick={onAddToSection} data-testid={`${testid}-section-${section.id}-add`} className="text-[10px] label-tiny !text-yellow-400 hover:underline px-2">+ EXERCISE</button>
          {collapsed ? <CaretDown size={12} className="text-text-secondary" /> : <CaretUp size={12} className="text-text-secondary" />}
        </div>
      </div>
      {!collapsed && (
        <div className="p-2">
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <div className="space-y-1.5">
              {exercises.map((ex) => (
                <ExerciseRow
                  key={`${section.id}-${ex.__idx}`}
                  id={`${section.id}::${ex.__idx}`}
                  ex={ex}
                  section={section}
                  testid={`${testid}-ex-${ex.__idx}`}
                  expanded={!!expanded[ex.__idx]}
                  onToggleExpand={() => setExpanded((s) => ({ ...s, [ex.__idx]: !s[ex.__idx] }))}
                  onChange={(k, v) => onPatch(ex.__idx, k, v)}
                  onRemove={() => onRemove(ex.__idx)}
                  onDuplicate={() => onDuplicate(ex.__idx)}
                  onMove={(sid) => onMove(ex.__idx, sid)}
                  onToggleFav={() => onToggleFav(ex.__idx)}
                  onCopyToDay={() => onCopyToDay(ex.__idx)}
                  onScheme={() => onScheme(ex)}
                />
              ))}
            </div>
          </SortableContext>
        </div>
      )}
    </div>
  );
}

/* ------------ Draggable exercise row ------------ */

function ExerciseRow({ id, ex, section, testid, expanded, onToggleExpand, onChange, onRemove, onDuplicate, onMove, onToggleFav, onCopyToDay, onScheme }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const [moveOpen, setMoveOpen] = useState(false);

  return (
    <div ref={setNodeRef} style={style} className="bg-surface-base/60 border border-white/5" data-testid={testid}>
      <div className="grid grid-cols-12 gap-1.5 items-center px-2 py-1.5">
        <button {...attributes} {...listeners} className="col-span-1 flex items-center justify-center cursor-grab active:cursor-grabbing text-text-secondary hover:!text-yellow-400" data-testid={`${testid}-drag`} aria-label="Drag exercise">
          <DotsSixVertical size={14} />
        </button>
        <Input className="col-span-4 bg-surface-elevated border-white/10 h-8 text-sm" placeholder="Exercise name" value={ex.name || ""} onChange={(e) => onChange("name", e.target.value)} data-testid={`${testid}-name`} />
        <Input className="col-span-1 bg-surface-elevated border-white/10 h-8 text-sm" type="number" placeholder="3" value={ex.sets ?? ""} onChange={(e) => onChange("sets", e.target.value)} data-testid={`${testid}-sets`} />
        <Input className="col-span-2 bg-surface-elevated border-white/10 h-8 text-sm" placeholder="reps" value={ex.reps ?? ""} onChange={(e) => onChange("reps", e.target.value)} data-testid={`${testid}-reps`} />
        <Input className="col-span-1 bg-surface-elevated border-white/10 h-8 text-sm" type="number" placeholder="rest s" value={ex.rest_sec ?? ""} onChange={(e) => onChange("rest_sec", e.target.value)} data-testid={`${testid}-rest`} />
        <div className="col-span-3 flex items-center justify-end gap-0.5">
          <button onClick={onToggleFav} data-testid={`${testid}-fav`} className={`p-1 hover:!text-yellow-400 transition ${ex.favorited ? "!text-yellow-400" : "text-text-secondary"}`} title="Favorite" aria-label="Favorite"><Star size={13} weight={ex.favorited ? "fill" : "regular"} /></button>
          <button onClick={onDuplicate} data-testid={`${testid}-duplicate`} className="p-1 text-text-secondary hover:!text-yellow-400 transition" title="Duplicate" aria-label="Duplicate"><Copy size={13} /></button>
          <div className="relative">
            <button onClick={() => setMoveOpen((v) => !v)} data-testid={`${testid}-move`} className="p-1 text-text-secondary hover:!text-yellow-400 transition" title="Move to section" aria-label="Move to section"><ArrowsOut size={13} /></button>
            {moveOpen && (
              <div className="absolute right-0 top-full mt-1 z-20 bg-surface-DEFAULT border border-white/10 shadow-lg py-1 min-w-[140px]" data-testid={`${testid}-move-menu`}>
                {SECTION_TYPES.filter((s) => s.id !== section.id).map((s) => (
                  <button key={s.id} onClick={() => { onMove(s.id); setMoveOpen(false); }} data-testid={`${testid}-move-${s.id}`} className="w-full text-left px-2 py-1 text-xs hover:bg-white/5 flex items-center gap-1.5">
                    <s.icon size={10} style={{ color: s.color }} /> {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={onCopyToDay} data-testid={`${testid}-copy-day`} className="p-1 text-text-secondary hover:!text-yellow-400 transition" title="Copy to another day" aria-label="Copy to day"><FolderSimplePlus size={13} /></button>
          <button onClick={onScheme} data-testid={`${testid}-scheme`} className="p-1 text-text-secondary hover:!text-yellow-400 transition" title="Apply progression scheme" aria-label="Apply progression scheme"><ChartLineUp size={13} /></button>
          <button onClick={onToggleExpand} data-testid={`${testid}-expand`} className={`p-1 transition ${expanded ? "!text-yellow-400" : "text-text-secondary hover:text-white"}`} title="Details" aria-label="Toggle details"><NotePencil size={13} /></button>
          <button onClick={onRemove} data-testid={`${testid}-remove`} className="p-1 text-text-secondary hover:text-red-400 transition" title="Remove" aria-label="Remove"><X size={13} /></button>
        </div>
      </div>

      {expanded && <ExerciseDetails ex={ex} testid={testid} onChange={onChange} />}
    </div>
  );
}

/* ------------ Exercise details drawer ------------ */

function ExerciseDetails({ ex, testid, onChange }) {
  const mode = (ex.mode || "").toLowerCase() || ((ex.section || "").toLowerCase() === "conditioning" ? "cardio" : "strength");
  return (
    <div className="border-t border-white/5 bg-surface-base/40 p-3 space-y-3" data-testid={`${testid}-details`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="label-tiny text-[9px] text-text-secondary">LOG AS</span>
        <div className="flex border border-white/10">
          <button type="button" onClick={() => onChange("mode", "strength")}
            className={`px-2.5 py-1 text-[10px] label-tiny transition-colors ${mode === "strength" ? "!bg-yellow-400/10 !text-yellow-400" : "text-text-secondary hover:text-white"}`}
            data-testid={`${testid}-mode-strength`}>SETS × REPS</button>
          <button type="button" onClick={() => onChange("mode", "cardio")}
            className={`px-2.5 py-1 text-[10px] label-tiny border-l border-white/10 transition-colors ${mode === "cardio" ? "!bg-yellow-400/10 !text-yellow-400" : "text-text-secondary hover:text-white"}`}
            data-testid={`${testid}-mode-cardio`}>CARDIO / CARRY</button>
        </div>
        {mode === "cardio" && <span className="text-[10px] text-text-secondary">Athlete logs time + distance (+ load)</span>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <F label="WEIGHT (KG)" value={ex.weight_kg} onChange={(v) => onChange("weight_kg", v)} placeholder="60" testid={`${testid}-weight`} />
        <F label="%1RM" value={ex.percentage_1rm} onChange={(v) => onChange("percentage_1rm", v)} placeholder="75" testid={`${testid}-pct`} />
        <F label="RPE" value={ex.rpe} onChange={(v) => onChange("rpe", v)} placeholder="8" testid={`${testid}-rpe`} />
        <F label="RIR" value={ex.rir} onChange={(v) => onChange("rir", v)} placeholder="2" testid={`${testid}-rir`} />
        <F label="TEMPO" value={ex.tempo} onChange={(v) => onChange("tempo", v)} placeholder="3-1-1-0" testid={`${testid}-tempo`} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <F label="PREVIOUS LOAD" value={ex.previous_load} onChange={(v) => onChange("previous_load", v)} placeholder="55 kg x 8" testid={`${testid}-prev`} />
        <F label="VELOCITY (M/S)" value={ex.velocity_target} onChange={(v) => onChange("velocity_target", v)} placeholder="0.8" testid={`${testid}-velo`} />
        <F label="DISTANCE (M)" value={ex.distance_m} onChange={(v) => onChange("distance_m", v)} placeholder="400" testid={`${testid}-dist`} />
        <F label="DURATION (S)" value={ex.duration_sec} onChange={(v) => onChange("duration_sec", v)} placeholder="60" testid={`${testid}-dur`} />
      </div>
      <div>
        <label className="label-tiny text-[9px] text-text-secondary block mb-1">COACH CUE</label>
        <Textarea rows={2} value={ex.coach_cue || ex.notes || ""} onChange={(e) => onChange("coach_cue", e.target.value)} placeholder="Pause at bottom, drive knees out" className="bg-surface-elevated border-white/10 text-sm" data-testid={`${testid}-cue`} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <VideoUrlField
          value={ex.video_url}
          onChange={(v) => onChange("video_url", v)}
          testid={`${testid}-video`}
        />
        <F label="GIF URL" value={ex.gif_url} onChange={(v) => onChange("gif_url", v)} placeholder="https://.../demo.gif" testid={`${testid}-gif`} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <F label="ALTERNATIVES" value={ex.alternatives} onChange={(v) => onChange("alternatives", v)} placeholder="DB Bench, Machine Press" testid={`${testid}-alts`} />
        <F label="REGRESSION" value={ex.regression || ex.regressions} onChange={(v) => onChange("regression", v)} placeholder="Push-ups, band-assist" testid={`${testid}-regr`} />
        <F label="PROGRESSION" value={ex.progression} onChange={(v) => onChange("progression", v)} placeholder="Pause, tempo, add band" testid={`${testid}-prog`} />
      </div>
      <F label="TAGS (COMMA SEPARATED)" value={ex.tags} onChange={(v) => onChange("tags", v)} placeholder="pull, unilateral, technique" testid={`${testid}-tags`} />
    </div>
  );
}

function VideoUrlField({ value, onChange, testid }) {
  const raw = value || "";
  // Normalize once on blur so the underlying model always stores the embed form
  // (drag/drop into other days, favorites, and downstream athlete rendering
  // all pick up the clean URL automatically).
  const handleBlur = () => {
    if (!raw) return;
    const normalized = normalizeVideoUrl(raw.trim());
    if (normalized && normalized !== raw) onChange(normalized);
  };
  const normalized = raw ? normalizeVideoUrl(raw.trim()) : "";
  const converted = raw && normalized && normalized !== raw;
  const canPreview = isEmbeddableVideoUrl(normalized);
  return (
    <div>
      <label className="label-tiny text-[9px] text-text-secondary block mb-1">
        VIDEO URL <span className="text-brand-heat/80 normal-case tracking-normal">— paste any youtube link</span>
      </label>
      <Input
        value={raw}
        onChange={(e) => onChange(e.target.value)}
        onBlur={handleBlur}
        placeholder="https://youtu.be/... or full watch/shorts URL"
        className="bg-surface-elevated border-white/10 h-8 text-sm"
        data-testid={testid}
      />
      {raw && (converted || canPreview || isVideoHostUrl(raw)) && (
        <div className="mt-1 space-y-1">
          {converted && (
            <div className="label-tiny text-[9px] text-brand-heat">
              // AUTO-CONVERTED · <span className="font-mono normal-case tracking-normal text-text-secondary">{normalized}</span>
            </div>
          )}
          {canPreview && (
            <div className="aspect-video w-full max-w-[280px] bg-black/40 border border-white/10" data-testid={`${testid}-preview`}>
              <iframe src={normalized} title="Preview" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function F({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="label-tiny text-[9px] text-text-secondary block mb-1">{label}</label>
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="bg-surface-elevated border-white/10 h-8 text-sm" data-testid={testid} />
    </div>
  );
}

function MetricPill({ label, value, unit, accent, testid }) {
  return (
    <div className={`border ${accent ? "!border-yellow-400 !bg-yellow-400/[0.05]" : "border-white/10"} p-2`} data-testid={testid}>
      <div className="label-tiny text-[9px] text-text-secondary">{label}</div>
      <div className={`font-heading tabular-nums text-lg ${accent ? "!text-yellow-400" : "text-white"}`}>{value}<span className="text-[10px] text-text-secondary ml-1">{unit}</span></div>
    </div>
  );
}
